import { FormEvent, useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  searchArchive,
  type SearchFilters,
  type SearchResponse,
  type Facet,
} from "../api";
import { ErrorState, LoadingState } from "../components/PageState";
import { WorkCard } from "../components/WorkCard";
import { usePageMeta } from "../hooks/usePageMeta";

import { Icon } from "../components/Icon";

const formats = [
  ["", "Everything"],
  ["audio-teaching", "Teachings"],
  ["written", "Books & articles"],
  ["music", "Music"],
  ["video", "Video"],
];
const empty: SearchResponse = {
  results: [],
  total: 0,
  nextCursor: null,
  facets: { media: [], topics: [], contributors: [], books: [] },
};
const labels: Record<string, string> = {
  medium: "Format",
  contributor: "Teacher or author",
  topic: "Topic",
  book: "Bible book",
  chapter: "Chapter",
  verse: "Verse",
  decade: "Decade",
  durationMax: "Length",
  availability: "Available to",
  sort: "Sort",
};
export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const key = params.toString();
  const currentKey = useRef(key);
  currentKey.current = key;
  const sheet = useRef<HTMLDialogElement>(null);
  const [query, setQuery] = useState(params.get("q") || "");
  const [data, setData] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [more, setMore] = useState(false);
  const [retry, setRetry] = useState(0);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  useEffect(() => {
    if (open) sheet.current?.showModal();
    else sheet.current?.close();
  }, [open]);
  const filters = Object.fromEntries(
    Object.keys(labels).map((k) => [k, params.get(k) || ""]),
  ) as SearchFilters;
  usePageMeta(
    query ? `Search: ${query}` : "Browse the library",
    "Find Christian teachings, music, books and Scripture studies.",
    "/search",
  );
  useEffect(() => {
    let active = true;
    setQuery(params.get("q") || "");
    setLoading(true);
    setError("");
    searchArchive(params.get("q") || "", filters)
      .then((d) => {
        if (active) setData(d);
      })
      .catch(() => {
        if (active)
          setError(
            "The library could not be searched. Your filters are saved. Please try again.",
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [key, retry]); // eslint-disable-line react-hooks/exhaustive-deps
  const change = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    if (k === "book") {
      next.delete("chapter");
      next.delete("verse");
    }
    setParams(next);
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    change("q", query.trim());
  };
  const activeFilters = Object.entries(filters).filter(
    ([k, v]) => v && k !== "sort",
  );
  const choose = (k: string, v: string) =>
    open
      ? setDraft((d) => ({
          ...d,
          [k]: v,
          ...(k === "book" ? { chapter: "", verse: "" } : {}),
        }))
      : change(k, v);
  const value = (k: string) => (open ? draft[k] || "" : params.get(k) || "");
  const select = (k: string, title: string, items: Facet[]) => (
    <label>
      {title}
      <select value={value(k)} onChange={(e) => choose(k, e.target.value)}>
        <option value="">All {title.toLowerCase()}</option>
        {value(k) && !items.some((x) => x.value === value(k)) ? (
          <option value={value(k)}>{value(k)}</option>
        ) : null}
        {items.map((x) => (
          <option key={x.value} value={x.value}>
            {x.label || x.value} ({x.count})
          </option>
        ))}
      </select>
    </label>
  );
  const controls = (
    <>
      {select("topic", "Topics", data.facets?.topics || [])}
      {select(
        "contributor",
        "Teachers & authors",
        data.facets?.contributors || [],
      )}
      {select("book", "Bible books", data.facets?.books || [])}
      {value("book") ? (
        <div className="filter-pair">
          <label>
            Chapter
            <input
              type="number"
              min="1"
              max="150"
              value={value("chapter")}
              onChange={(e) => choose("chapter", e.target.value)}
            />
          </label>
          <label>
            Verse
            <input
              type="number"
              min="1"
              max="176"
              value={value("verse")}
              onChange={(e) => choose("verse", e.target.value)}
            />
          </label>
        </div>
      ) : null}
      <label>
        Listening time
        <select
          value={value("durationMax")}
          onChange={(e) => choose("durationMax", e.target.value)}
        >
          <option value="">Any length</option>
          <option value="15">Under 15 minutes</option>
          <option value="30">Under 30 minutes</option>
          <option value="60">Under an hour</option>
        </select>
      </label>
      <label>
        Catalog decade
        <select
          value={value("decade")}
          onChange={(e) => choose("decade", e.target.value)}
        >
          <option value="">Any decade</option>
          {[1930, 1940, 1950, 1960, 1970, 1980, 1990, 2000, 2010, 2020].map(
            (n) => (
              <option key={n} value={n}>
                {n}s
              </option>
            ),
          )}
        </select>
      </label>
      <label>
        Available to
        <select
          value={value("availability")}
          onChange={(e) => choose("availability", e.target.value)}
        >
          <option value="">Listen, read or watch</option>
          <option value="playable">Listen or watch</option>
          <option value="readable">Read</option>
        </select>
      </label>
      <p className="filter-note">
        Dates reflect catalog metadata and may refer to publication rather than
        recording. Unknown dates and durations are excluded when filtering.
      </p>
    </>
  );
  return (
    <div className="search-page">
      <header className="page-heading">
        <h1>Browse the library</h1>
      </header>
      <form className="search-page__form" onSubmit={submit}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search a title, topic or teacher"
          aria-label="Search query"
        />
        <button>Search</button>
      </form>
      <label className="mobile-format">
        Format
        <select
          aria-label="Media format"
          value={filters.medium || ""}
          onChange={(e) => change("medium", e.target.value)}
        >
          {formats.map(([v, label]) => (
            <option key={v} value={v}>
              {label}
            </option>
          ))}
        </select>
      </label>
      <div className="format-tabs" aria-label="Media format">
        {formats.map(([v, l]) => (
          <button
            key={v}
            aria-pressed={(filters.medium || "") === v}
            onClick={() => change("medium", v)}
          >
            {l}
          </button>
        ))}
      </div>
      <div className="browse-layout">
        <aside className="browse-filters">
          <h2>Find your way</h2>
          {controls}
        </aside>
        <section className="browse-results" aria-label="Search results">
          <div className="results-heading">
            <p aria-live="polite">
              {loading
                ? "Searching…"
                : `${data.total.toLocaleString()} results`}
            </p>
            <button
              className="mobile-filters"
              onClick={() => {
                setDraft(Object.fromEntries(params));
                setOpen(true);
              }}
            >
              <Icon name="filters" /> Filters & sort
              {activeFilters.filter(([k]) => k !== "medium").length
                ? ` (${activeFilters.filter(([k]) => k !== "medium").length})`
                : ""}
            </button>
            <label className="desktop-sort">
              Sort
              <select
                aria-label="Sort results"
                value={filters.sort || ""}
                onChange={(e) => change("sort", e.target.value)}
              >
                <option value="">{query ? "Relevance" : "Title"}</option>
                <option value="title">Title A–Z</option>
                <option value="added">Recently added</option>
                <option value="newest">Catalog year: newest first</option>
                <option value="oldest">Catalog year: oldest first</option>
              </select>
            </label>
          </div>
          {activeFilters.some(([k]) => k !== "medium") ? (
            <div className="filter-chips">
              {activeFilters
                .filter(([k]) => k !== "medium")
                .map(([k, v]) => (
                  <button
                    key={k}
                    onClick={() => change(k, "")}
                    aria-label={`Remove ${labels[k]} filter`}
                  >
                    {labels[k]}: {v} ×
                  </button>
                ))}
              <button onClick={() => setParams(query ? { q: query } : {})}>
                Clear filters
              </button>
            </div>
          ) : null}
          {loading ? (
            <LoadingState label="Finding your next discovery…" />
          ) : error ? (
            <>
              <ErrorState message={error} />
              <button onClick={() => setRetry((n) => n + 1)}>Try again</button>
            </>
          ) : data.results.length ? (
            <>
              <div className="search-results">
                {data.results.map((hit) => (
                  <div key={hit.work.id}>
                    <WorkCard
                      work={hit.work}
                      compact
                      reason={hit.matchReasons.join(", ")}
                    />
                    {hit.excerpt ? (
                      <p className="search-result__excerpt">
                        {hit.excerpt.replace(/<[^>]+>/g, "")}
                        {hit.startSeconds != null ? (
                          <Link
                            to={`/works/${hit.work.slug}?t=${Math.floor(hit.startSeconds)}`}
                          >
                            {" "}
                            Listen from {Math.floor(hit.startSeconds / 60)}:
                            {String(Math.floor(hit.startSeconds % 60)).padStart(
                              2,
                              "0",
                            )}{" "}
                            →
                          </Link>
                        ) : null}
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
              {data.nextCursor ? (
                <button
                  className="load-more"
                  disabled={more}
                  onClick={async () => {
                    setMore(true);
                    try {
                      const next = await searchArchive(
                        query,
                        filters,
                        data.nextCursor,
                      );
                      if (currentKey.current !== key) return;
                      setData((d) => ({
                        ...next,
                        results: [...d.results, ...next.results],
                      }));
                    } catch {
                      setError(
                        "More results could not be loaded. Please try again.",
                      );
                    } finally {
                      setMore(false);
                    }
                  }}
                >
                  {more ? "Loading…" : "Show more"}
                </button>
              ) : null}
            </>
          ) : (
            <div className="empty-state">
              <h2>A little more room to explore.</h2>
              <p>
                No items match this combination. Try removing a filter or
                searching for a teacher’s surname.
              </p>
              <button onClick={() => setParams(query ? { q: query } : {})}>
                Clear filters
              </button>
              <Link to="/">Explore selected collections</Link>
            </div>
          )}
        </section>
      </div>
      {open ? (
        <dialog
          ref={sheet}
          onClose={() => setOpen(false)}
          className="filter-sheet"
          aria-modal="true"
          aria-label="Filters"
        >
          <header>
            <h2>Refine your search</h2>
            <button
              autoFocus
              aria-label="Close filters"
              onClick={() => setOpen(false)}
            >
              Close
            </button>
          </header>
          <label>
            Sort results
            <select
              aria-label="Sort results"
              value={draft.sort || ""}
              onChange={(e) => choose("sort", e.target.value)}
            >
              <option value="">{query ? "Relevance" : "Title"}</option>
              <option value="title">Title A–Z</option>
              <option value="added">Recently added</option>
              <option value="newest">Catalog year: newest first</option>
              <option value="oldest">Catalog year: oldest first</option>
            </select>
          </label>
          {controls}
          <footer className="filter-sheet__footer">
            <button
              className="filter-reset"
              onClick={() => setDraft({ medium: draft.medium || "" })}
            >
              Reset
            </button>
            <button
              className="primary-action"
              onClick={() => {
                const next = new URLSearchParams(params);
                for (const k of Object.keys(labels)) {
                  if (draft[k]) next.set(k, draft[k]);
                  else next.delete(k);
                }
                setParams(next);
                setOpen(false);
              }}
            >
              Apply filters
            </button>
          </footer>
        </dialog>
      ) : null}
    </div>
  );
}
